import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, ChevronDown, ShieldCheck, Battery, Zap, Layers, Power, ArrowRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";

export const products = [
  {
    id: "tesla-powerwall-3",
    name: "Tesla Powerwall 3",
    badge: "Most popular",
    capacity: "13.5 kWh",
    capacityRange: "13,5 kWh",
    warranty: "10 jaar",
    backup: "Volledig",
    expandable: "Tot 4 units",
    powerKw: "11,5 kW continu",
    indicativeSavings: "tot € 1.700 / jaar",
    price: "vanaf € 9.490",
    image: "/images/batteries/tesla-powerwall-3.png",
    highlights: ["Geïntegreerde omvormer", "Stormbeveiliging", "App-monitoring"],
    specs: {
      "Bruikbare capaciteit": "13,5 kWh",
      "Continu vermogen": "11,5 kW",
      "Piekvermogen": "Tot 185 A",
      "Omvormer": "Geïntegreerd (hybride)",
      "Schaalbaar": "Tot 4 units (54 kWh)",
      "Gewicht": "130 kg",
      "Afmetingen": "1105 × 609 × 193 mm",
    },
    pros: [
      "Geïntegreerde hybride omvormer",
      "Black-out backup voor het hele huis",
      "Premium app met realtime monitoring",
    ],
    cons: [
      "Hogere instapprijs dan modulair",
      "Levertijd kan oplopen tot 6 weken",
    ],
  },
  {
    id: "huawei-luna2000",
    name: "Huawei Luna2000",
    badge: "Modulair",
    capacity: "5 – 30 kWh",
    capacityRange: "5 t/m 30 kWh",
    warranty: "10 jaar",
    backup: "Optioneel",
    expandable: "Stapelbaar 5, 10, 15 kWh",
    powerKw: "5 kW per module",
    indicativeSavings: "tot € 1.500 / jaar",
    price: "vanaf € 5.290",
    image: "/images/batteries/huawei-luna2000.png",
    highlights: ["Modulair systeem", "Hoog vermogen", "AI-energiebeheer"],
    specs: {
      "Bruikbare capaciteit": "5, 10, 15 of 30 kWh",
      "Continu vermogen": "2,5 – 5 kW per module",
      "Omvormer": "Compatible met SUN2000",
      "Schaalbaar": "Modulair, breid achteraf uit",
      "Gewicht": "50 kg per module",
      "Afmetingen": "670 × 600 × 240 mm per module",
    },
    pros: [
      "Begin klein, breid later eenvoudig uit",
      "Slimme AI-energiemanager",
      "Geschikt voor dynamische tarieven",
    ],
    cons: [
      "Backup-functie als optie",
      "Vereist passende Huawei SUN2000 omvormer",
    ],
  },
  {
    id: "byd-battery-box",
    name: "BYD Battery-Box Premium HVS",
    badge: "Beste prijs/kWh",
    capacity: "10 – 22 kWh",
    capacityRange: "5 t/m 22 kWh",
    warranty: "10 jaar (80% capaciteit)",
    backup: "Optioneel",
    expandable: "Stapelbaar 2,56 kWh",
    powerKw: "Tot 11 kW continu",
    indicativeSavings: "tot € 1.600 / jaar",
    price: "vanaf € 6.890",
    image: "/images/batteries/byd-battery-box.png",
    highlights: ["LFP-cellen (veilig)", "Lange levensduur", "Schaalbaar"],
    specs: {
      "Bruikbare capaciteit": "5,1 – 22,1 kWh",
      "Continu vermogen": "Tot 11 kW",
      "Celtype": "LFP (LiFePO₄, kobaltvrij)",
      "Schaalbaar": "Modulair (2,56 kWh per module)",
      "Gewicht": "Ca. 40 kg per module",
      "Afmetingen": "585 × 298 × 233 mm per module",
    },
    pros: [
      "Veilige LFP-celchemie, geen kobalt",
      "Zeer scherpe prijs per kWh",
      "Werkt met diverse hybride omvormers",
    ],
    cons: [
      "Aparte hybride omvormer vereist",
      "Backup-functie als optie",
    ],
  },
];

/* --- Accordion row --- */
const AccordionRow = ({ icon: Icon, label, value, isOpen, onToggle, children }) => (
  <div className="border-t border-slate-100">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between py-3.5 text-left"
      data-testid={`acc-toggle-${label.toLowerCase().replace(/\s+/g, "-")}`}
      aria-expanded={isOpen}
    >
      <span className="flex items-center gap-2.5 text-sm">
        <Icon size={15} className="text-brand" />
        <span className="font-semibold text-navy-900">{label}</span>
      </span>
      <span className="flex items-center gap-2 text-slate-500">
        {value && <span className="text-xs text-slate-500 hidden sm:inline">{value}</span>}
        <ChevronDown size={16} className={`text-slate-400 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
      </span>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <div className="pb-4 text-sm text-slate-600 leading-relaxed">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

/* --- Single card --- */
const ProductCard = ({ p, i }) => {
  const [openKey, setOpenKey] = useState(null);
  const toggle = (k) => setOpenKey((c) => (c === k ? null : k));

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay: i * 0.08 }}
      whileHover={{ y: -8 }}
      whileTap={{ scale: 0.99 }}
      className="group relative bg-white rounded-[2rem] border border-slate-200 overflow-hidden transition-all duration-[400ms] ease-out hover:shadow-[0_40px_100px_rgba(255,47,125,0.16)] hover:border-brand/30"
      data-testid={`product-card-${i}`}
    >
      {/* hover gradient wash */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-[400ms] bg-gradient-to-br from-brand-soft/60 via-white to-white pointer-events-none" />

      <div className="relative p-6 lg:p-7">
        {/* badge */}
        <div className="absolute top-6 left-6 z-10">
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-navy-900 text-white text-[10px] font-bold tracking-[0.18em] uppercase">
            {p.badge}
          </span>
        </div>

        {/* product image — clean studio bg */}
        <div className="relative aspect-square bg-gradient-to-br from-slate-50 to-brand-soft/40 rounded-2xl mb-6 overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="absolute w-44 h-44 rounded-full bg-brand/15 blur-3xl group-hover:bg-brand/30 transition-colors duration-700" />
            <img
              src={p.image}
              alt={p.name}
              loading="lazy"
              className="relative w-full h-full object-cover transition-transform duration-[500ms] ease-out group-hover:scale-[1.04]"
            />
          </div>
        </div>

        <h3 className="font-heading font-black text-2xl text-navy-900 leading-tight">{p.name}</h3>
        <div className="text-brand font-semibold text-sm mt-1 mb-4">{p.capacity}</div>

        <ul className="space-y-2 mb-5">
          {p.highlights.map((f) => (
            <li key={f} className="flex items-center gap-2 text-sm text-slate-600">
              <span className="w-5 h-5 rounded-full bg-brand-soft flex items-center justify-center flex-shrink-0">
                <Check size={11} className="text-brand" strokeWidth={3} />
              </span>
              {f}
            </li>
          ))}
        </ul>

        {/* --- ACCORDION --- */}
        <div className="rounded-xl bg-slate-50/70 border border-slate-100 px-4 mt-1">
          <AccordionRow icon={Layers} label="Specificaties" isOpen={openKey === "specs"} onToggle={() => toggle("specs")}>
            <dl className="grid grid-cols-[140px_1fr] gap-y-1.5 gap-x-3">
              {Object.entries(p.specs).map(([k, v]) => (
                <React.Fragment key={k}>
                  <dt className="text-slate-500">{k}</dt>
                  <dd className="text-navy-900 font-medium">{v}</dd>
                </React.Fragment>
              ))}
            </dl>
          </AccordionRow>

          <AccordionRow icon={ShieldCheck} label="Garantie" value={p.warranty} isOpen={openKey === "warranty"} onToggle={() => toggle("warranty")}>
            Fabrieksgarantie {p.warranty} op de batterij. 5 jaar installatie- en serviceondersteuning vanuit Helios Bright.
          </AccordionRow>

          <AccordionRow icon={Power} label="Back-up functie" value={p.backup} isOpen={openKey === "backup"} onToggle={() => toggle("backup")}>
            {p.backup === "Volledig"
              ? "Voedt het volledige huis bij stroomuitval. Automatische overschakeling binnen milliseconden."
              : "Backup is leverbaar als optionele uitbreiding. Vraag onze adviseur naar de mogelijkheden voor jouw meterkast."}
          </AccordionRow>

          <AccordionRow icon={Zap} label="Laadvermogen" value={p.powerKw} isOpen={openKey === "power"} onToggle={() => toggle("power")}>
            Continu vermogen: <b>{p.powerKw}</b>. Voldoende voor moderne huishoudens met warmtepomp, elektrisch koken en EV laden.
          </AccordionRow>

          <AccordionRow icon={Battery} label="Voor- en nadelen" isOpen={openKey === "pros"} onToggle={() => toggle("pros")}>
            <div className="grid gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-widest font-bold text-emerald-600 mb-1.5">Voordelen</p>
                <ul className="space-y-1">
                  {p.pros.map((x) => (
                    <li key={x} className="flex items-start gap-2"><Check size={13} className="text-emerald-500 mt-0.5 flex-shrink-0" strokeWidth={3} />{x}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">Aandachtspunten</p>
                <ul className="space-y-1">
                  {p.cons.map((x) => (
                    <li key={x} className="flex items-start gap-2"><span className="w-3 h-[2px] bg-slate-400 mt-2 flex-shrink-0" />{x}</li>
                  ))}
                </ul>
              </div>
            </div>
          </AccordionRow>
        </div>

        {/* Footer: price + CTA */}
        <div className="mt-6 flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Investering</div>
            <div className="font-heading font-black text-navy-900 text-lg leading-tight">{p.price}</div>
          </div>
          <Link
            to="/contact"
            state={{ product: p.name }}
            className="btn-pink !text-sm !py-2.5 !px-4 whitespace-nowrap"
            data-testid={`product-cta-${i}`}
          >
            Offerte <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </motion.article>
  );
};

const ProductShowcase = () => {
  const { t } = useLang();
  return (
    <section className="section-pad bg-slate-50/70 relative" data-testid="product-showcase">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">{t.products.eyebrow}</p>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">{t.products.title}</h2>
          <p className="mt-5 text-slate-600 text-lg">{t.products.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {products.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
